import React, { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem('flix_install_dismissed') === '1') return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS never fires beforeinstallprompt — show manual hint after a delay
    let t;
    if (isIos()) {
      t = setTimeout(() => setShow(true), 2500);
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setShowIosSheet(false);
    localStorage.setItem('flix_install_dismissed', '1');
  };

  const install = async () => {
    if (isIos()) {
      setShowIosSheet(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 z-[90]">
        <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(15,23,18,0.25)] border border-[#0f1712]/8 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#0e9f6e] flex items-center justify-center shrink-0">
            <span className="brand text-white text-lg leading-none">F</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#0f1712] leading-tight">Install FLIX</p>
            <p className="text-xs text-[#0f1712]/55">Add to your home screen for quick access.</p>
          </div>
          <button onClick={install} className="inline-flex items-center gap-1.5 bg-[#0e9f6e] text-white rounded-xl px-3.5 py-2 text-xs font-semibold hover:bg-[#0c8a60] transition-colors shrink-0">
            <Download size={14} /> Install
          </button>
          <button onClick={dismiss} className="text-[#0f1712]/35 hover:text-[#0f1712] transition-colors shrink-0" aria-label="dismiss">
            <X size={18} />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#0b1510]/60 backdrop-blur-sm" onClick={() => setShowIosSheet(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-3xl p-7 shadow-2xl rise-in">
            <button onClick={() => setShowIosSheet(false)} className="absolute top-5 right-5 text-[#0f1712]/40 hover:text-[#0f1712]">
              <X size={20} />
            </button>
            <div className="w-12 h-12 rounded-2xl bg-[#0e9f6e] flex items-center justify-center mb-5">
              <span className="brand text-white text-xl">F</span>
            </div>
            <h3 className="font-display text-xl font-700 text-[#0f1712]">Install on iPhone</h3>
            <p className="text-sm text-[#0f1712]/55 mt-1 mb-5">Add FLIX to your home screen in two steps:</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-[#0f1712]/80">
                <span className="w-7 h-7 rounded-full bg-[#f6f6f2] flex items-center justify-center font-semibold text-[#0e9f6e]">1</span>
                Tap the <Share size={16} className="text-[#0e9f6e]" /> Share button in Safari
              </div>
              <div className="flex items-center gap-3 text-sm text-[#0f1712]/80">
                <span className="w-7 h-7 rounded-full bg-[#f6f6f2] flex items-center justify-center font-semibold text-[#0e9f6e]">2</span>
                Choose <Plus size={16} className="text-[#0e9f6e]" /> “Add to Home Screen”
              </div>
            </div>
            <button onClick={dismiss} className="w-full mt-6 bg-[#0f1712] text-white rounded-xl px-5 py-3 text-sm font-semibold">Got it</button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
